import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  generateEmbedding,
  profileToEmbeddingText,
  toPgVector,
  EmbeddingError,
} from "./lib/embeddings";

/**
 * Backfill embeddings for the current user's profile and all their stored JDs.
 * Idempotent — only generates embeddings where missing.
 */
export const backfillEmbeddings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    let profileUpdated = false;
    let jdsUpdated = 0;
    const errors: string[] = [];

    // 1. Profile embedding
    const { data: profile } = await supabase
      .from("candidate_profiles")
      .select(
        "verified_skills, chronological_experience, raw_resume_text, github_username, embedding",
      )
      .eq("user_id", userId)
      .maybeSingle();

    const hasProfileEmbedding = !!(profile as { embedding?: unknown } | null)?.embedding;

    if (profile && !hasProfileEmbedding) {
      const text = profileToEmbeddingText(profile);
      if (text.trim()) {
        try {
          const vec = await generateEmbedding(text);
          const { error } = await supabase
            .from("candidate_profiles")
            .update({
              embedding: toPgVector(vec) as unknown as never,
              embedding_updated_at: new Date().toISOString(),
            })
            .eq("user_id", userId);
          if (error) errors.push(`Profile: ${error.message}`);
          else profileUpdated = true;
        } catch (e) {
          const msg = e instanceof EmbeddingError ? e.message : String(e);
          errors.push(`Profile embedding: ${msg}`);
        }
      }
    }

    // 2. JD embeddings (where missing)
    const { data: jds } = await supabase
      .from("job_descriptions")
      .select("id, jd_text, embedding")
      .eq("user_id", userId);

    for (const jd of jds ?? []) {
      const hasEmbedding = !!(jd as { embedding?: unknown }).embedding;
      if (hasEmbedding) continue;
      try {
        const vec = await generateEmbedding(jd.jd_text);
        const { error } = await supabase
          .from("job_descriptions")
          .update({
            embedding: toPgVector(vec) as unknown as never,
            embedding_updated_at: new Date().toISOString(),
          })
          .eq("id", jd.id);
        if (error) errors.push(`JD ${jd.id}: ${error.message}`);
        else jdsUpdated++;
      } catch (e) {
        const msg = e instanceof EmbeddingError ? e.message : String(e);
        errors.push(`JD ${jd.id}: ${msg}`);
      }
    }

    return { profileUpdated, jdsUpdated, errors };
  });
